import { Numeric } from "@rothoblaas/design-system";
import { memo, useEffect, useMemo, useRef, useState, type CSSProperties, type FC, type KeyboardEvent } from "react";
import useI18n from "../../../hooks/useTranslations";
import type { PlaneBounds, PlaneCoordinate, PlaneScreenPoint, ToScreenPoint } from "./CartesianPlane";
import styles from "./DeckEdgeMeasurements.module.css";
import ImperialLengthInput from "./ImperialLengthInput";
import { useMeasurementFormat, type MeasurementFormat } from "./measurementFormat";
import {
  getCoordinateBounds,
  getLShapeDimensions,
  getSegmentLength,
  hasMatchingDimension,
  isMostlyHorizontal,
  type LShapeDimensions,
} from "./deckGeometry";
import { getNumberFormat } from "../../../utils/numberFormat";

const LABEL_OFFSET_PX = 18;
const MIN_MEASUREMENT_LENGTH = 0.005;

// Technical side codes shown next to the measurement, matching the labels in ShapeAndSizeCard
// (e.g. "Max width (L1)"). Not translated - same abbreviations across every language.
const PRIMARY_WIDTH_CODE = "L1";
const PRIMARY_HEIGHT_CODE = "L2";
const SECONDARY_WIDTH_CODE = "l1";
const SECONDARY_HEIGHT_CODE = "l2";

export type MeasurementSegment = {
  end: PlaneCoordinate;
  id: string;
  isPreview: boolean;
  start: PlaneCoordinate;
};

type DeckEdgeMeasurementsProps = {
  coordinates: PlaneCoordinate[];
  maxCoordinates: number;
  onCommitSideValue?: (sideCode: string, value: number) => boolean;
  previewCoordinate?: PlaneCoordinate | null;
  toScreenPoint: ToScreenPoint;
  // Renders larger labels for the PDF export capture only - the interactive editor keeps its normal size.
  exportMode?: boolean;
};

export const getMeasurementSegments = (
  coordinates: PlaneCoordinate[],
  maxCoordinates: number,
  previewCoordinate?: PlaneCoordinate | null,
): MeasurementSegment[] => {
  const segments: MeasurementSegment[] = [];

  for (let index = 0; index < coordinates.length - 1; index += 1) {
    const start = coordinates[index];
    const end = coordinates[index + 1];

    if (start && end) {
      segments.push({
        id: `confirmed-${index}`,
        start,
        end,
        isPreview: false,
      });
    }
  }

  const firstCoordinate = coordinates[0];
  const lastCoordinate = coordinates[coordinates.length - 1];

  if (coordinates.length === maxCoordinates && firstCoordinate && lastCoordinate) {
    segments.push({
      id: "confirmed-close",
      start: lastCoordinate,
      end: firstCoordinate,
      isPreview: false,
    });
  }

  if (
    previewCoordinate &&
    coordinates.length > 0 &&
    coordinates.length < maxCoordinates &&
    firstCoordinate &&
    lastCoordinate
  ) {
    segments.push({
      id: "preview-next",
      start: lastCoordinate,
      end: previewCoordinate,
      isPreview: true,
    });

    if (coordinates.length + 1 === maxCoordinates) {
      segments.push({
        id: "preview-close",
        start: previewCoordinate,
        end: firstCoordinate,
        isPreview: true,
      });
    }
  }

  return segments.filter((segment) => getSegmentLength(segment.start, segment.end) > MIN_MEASUREMENT_LENGTH);
};

const getMeasurementPosition = (
  segment: MeasurementSegment,
  startPoint: PlaneScreenPoint,
  endPoint: PlaneScreenPoint,
  bounds: PlaneBounds,
  offsetPx: number,
): { left: number; top: number } => {
  const midpointPixelX = (startPoint.pixelX + endPoint.pixelX) / 2;
  const midpointPixelY = (startPoint.pixelY + endPoint.pixelY) / 2;
  const midpointCoordinateX = (segment.start.x + segment.end.x) / 2;
  const midpointCoordinateY = (segment.start.y + segment.end.y) / 2;
  const centerX = (bounds.minX + bounds.maxX) / 2;
  const centerY = (bounds.minY + bounds.maxY) / 2;

  if (isMostlyHorizontal(segment.start, segment.end)) {
    return {
      left: midpointPixelX,
      top: midpointPixelY + (midpointCoordinateY >= centerY ? -offsetPx : offsetPx),
    };
  }

  return {
    left: midpointPixelX + (midpointCoordinateX >= centerX ? offsetPx : -offsetPx),
    top: midpointPixelY,
  };
};

// Matches a confirmed edge's length against the shape's named dimensions (base/height/secondary
// base/secondary height) to show its corresponding side code, e.g. "3.20 m (L1)". An L-shape's
// two "remainder" edges (full side minus the cutout) don't match any single named dimension, so
// they're left unlabeled.
export const getSideCode = (segment: MeasurementSegment, dimensions: LShapeDimensions): string | null => {
  if (segment.isPreview) {
    return null;
  }

  const length = getSegmentLength(segment.start, segment.end);

  if (isMostlyHorizontal(segment.start, segment.end)) {
    if (hasMatchingDimension(dimensions.width, length)) {
      return PRIMARY_WIDTH_CODE;
    }
    if (hasMatchingDimension(dimensions.secondaryWidth, length)) {
      return SECONDARY_WIDTH_CODE;
    }
    return null;
  }

  if (hasMatchingDimension(dimensions.height, length)) {
    return PRIMARY_HEIGHT_CODE;
  }
  if (hasMatchingDimension(dimensions.secondaryHeight, length)) {
    return SECONDARY_HEIGHT_CODE;
  }
  return null;
};

export type EdgeMeasurementLabel = {
  key: string;
  point: PlaneCoordinate;
  orientation: "horizontal" | "vertical";
  rotate: boolean;
  lengthMeters: number;
  sideCode: string | null;
};

const formatSegmentLength = (
  lengthMeters: number,
  formatter: Intl.NumberFormat,
  unitM: string,
  measurementFormat: MeasurementFormat,
): string => measurementFormat.length(lengthMeters, "m", `${formatter.format(lengthMeters)} ${unitM}`);

// Pure data version of what this component renders, for the confirmed (non-preview) edges only -
// used by the PDF export, whose server draws the same labels as crisp vector text instead of baking
// them into the captured raster. Kept intentionally independent of the component's own interactive
// state, and of any language: the server formats the length.
export const getEdgeMeasurementLabels = (
  coordinates: PlaneCoordinate[],
  maxCoordinates: number,
): EdgeMeasurementLabel[] => {
  const dimensions = getLShapeDimensions(coordinates);

  return getMeasurementSegments(coordinates, maxCoordinates).map((segment) => {
    const isHorizontal = isMostlyHorizontal(segment.start, segment.end);

    return {
      key: segment.id,
      point: {
        x: (segment.start.x + segment.end.x) / 2,
        y: (segment.start.y + segment.end.y) / 2,
      },
      orientation: isHorizontal ? "horizontal" : "vertical",
      // A vertical edge has ample vertical room and tight horizontal room in the padded margin -
      // rotating the text to run alongside it uses that space better than a wide horizontal box.
      rotate: !isHorizontal,
      lengthMeters: getSegmentLength(segment.start, segment.end),
      sideCode: getSideCode(segment, dimensions),
    };
  });
};

const DeckEdgeMeasurements: FC<DeckEdgeMeasurementsProps> = ({
  coordinates,
  maxCoordinates,
  onCommitSideValue,
  previewCoordinate,
  toScreenPoint,
  exportMode = false,
}) => {
  const i18n = useI18n();
  const languageCode = i18n.translation.languageCode ?? undefined;
  const [editingSegmentId, setEditingSegmentId] = useState<string | null>(null);
  const [editSideCode, setEditSideCode] = useState<string | null>(null);
  const [editValue, setEditValue] = useState<number | null>(null);
  const [isEditInvalid, setIsEditInvalid] = useState(false);
  const measurementFormat = useMeasurementFormat();
  const editRef = useRef<HTMLDivElement>(null);

  // MeasurementInput has no autoFocus: focus the imperial edit field once it opens.
  useEffect(() => {
    if (editingSegmentId && measurementFormat.isImperial) {
      editRef.current?.querySelector("input")?.focus();
    }
  }, [editingSegmentId, measurementFormat.isImperial]);
  const bounds = useMemo(
    () =>
      getCoordinateBounds(
        previewCoordinate && coordinates.length < maxCoordinates ? [...coordinates, previewCoordinate] : coordinates,
      ),
    [coordinates, maxCoordinates, previewCoordinate],
  );
  const formatter = useMemo(
    () =>
      getNumberFormat(languageCode, {
        maximumFractionDigits: 3,
        minimumFractionDigits: 2,
      }),
    [languageCode],
  );
  const segments = useMemo(
    () => getMeasurementSegments(coordinates, maxCoordinates, previewCoordinate),
    [coordinates, maxCoordinates, previewCoordinate],
  );
  const dimensions = useMemo(() => getLShapeDimensions(coordinates), [coordinates]);

  // The export capture carries no text at all - the PDF draws these labels itself afterwards, as
  // vector text sized to match its own tables (see getEdgeMeasurementLabels), so there's nothing
  // for the plane's overflow:hidden to clip regardless of how close to the edge a label would sit.
  if (!bounds || exportMode) {
    return null;
  }

  const stopEditing = () => {
    setEditingSegmentId(null);
    setEditSideCode(null);
    setEditValue(null);
    setIsEditInvalid(false);
  };

  const commitEdit = () => {
    if (!editSideCode || editValue == null || editValue <= 0) {
      setIsEditInvalid(true);
      return;
    }

    const accepted = onCommitSideValue?.(editSideCode, editValue) ?? true;

    if (accepted) {
      stopEditing();
    } else {
      setIsEditInvalid(true);
    }
  };

  const commitOrDiscardOnBlur = () => {
    if (editSideCode && editValue != null && editValue > 0) {
      onCommitSideValue?.(editSideCode, editValue);
    }
    stopEditing();
  };

  const startEditing = (segment: MeasurementSegment, sideCode: string) => {
    setEditingSegmentId(segment.id);
    setEditSideCode(sideCode);
    setEditValue(getSegmentLength(segment.start, segment.end));
    setIsEditInvalid(false);
  };

  const handleEditKeyDown = (event: KeyboardEvent<HTMLElement>) => {
    if (event.key === "Enter") {
      event.preventDefault();
      commitEdit();
    } else if (event.key === "Escape") {
      event.preventDefault();
      stopEditing();
    }
  };

  return (
    <>
      {segments.map((segment) => {
        const startPoint = toScreenPoint(segment.start);
        const endPoint = toScreenPoint(segment.end);

        if (!startPoint || !endPoint) {
          return null;
        }

        const position = getMeasurementPosition(segment, startPoint, endPoint, bounds, LABEL_OFFSET_PX);
        const style: CSSProperties = {
          left: position.left,
          top: position.top,
        };
        const sideCode = getSideCode(segment, dimensions);
        const isEditable = !!sideCode && !!onCommitSideValue;

        if (editingSegmentId === segment.id) {
          return (
            <div
              key={segment.id}
              className={`${styles.measurementEdit} ${isEditInvalid ? styles.invalidEdit : ""}`}
              style={style}
              ref={editRef}
              onPointerDown={(event) => event.stopPropagation()}
              onBlur={commitOrDiscardOnBlur}
              // MeasurementInput takes no onKeyDown: the imperial field's keys bubble up to here.
              onKeyDown={measurementFormat.isImperial ? handleEditKeyDown : undefined}
            >
              {measurementFormat.isImperial ? (
                <ImperialLengthInput
                  value={editValue}
                  onValueChange={(value) => {
                    setEditValue(value);
                    setIsEditInvalid(false);
                  }}
                  metricUnit="m"
                  imperialUnit="ft"
                  metricDecimalScale={3}
                  maxDenominator={8}
                  isInvalid={isEditInvalid}
                />
              ) : (
                <Numeric
                  autoFocus
                  value={editValue}
                  decimalScale={3}
                  suffix={` ${i18n.translation.common.unitM}`}
                  allowNegative={false}
                  isInvalid={isEditInvalid}
                  onValueChange={(value) => {
                    setEditValue(value);
                    setIsEditInvalid(false);
                  }}
                  onKeyDown={handleEditKeyDown}
                />
              )}
            </div>
          );
        }

        return (
          <div
            key={segment.id}
            aria-hidden={!sideCode || undefined}
            className={`${styles.measurement} ${segment.isPreview ? styles.previewMeasurement : ""} ${
              isEditable ? styles.editableMeasurement : ""
            } ${!isEditable && !segment.isPreview ? styles.nonEditableMeasurement : ""}`}
            style={style}
            onPointerDown={sideCode ? (event) => event.stopPropagation() : undefined}
            onClick={
              isEditable
                ? (event) => {
                    event.stopPropagation();
                    startEditing(segment, sideCode);
                  }
                : undefined
            }
          >
            {formatSegmentLength(
              getSegmentLength(segment.start, segment.end),
              formatter,
              i18n.translation.common.unitM,
              measurementFormat,
            )}
            {sideCode ? ` ( ${sideCode} )` : ""}
          </div>
        );
      })}
    </>
  );
};

// Memoized: re-renders only when its edges, preview or the view change, not with every render of the
// drawing around it.
export default memo(DeckEdgeMeasurements);
